import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { postsAPI, categoriesAPI } from '../services/api'
import { Post, Category } from '../types'
import './CategoryPage.css'

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [posts, setPosts] = useState<Post[]>([])
  const [category, setCategory] = useState<Category | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const postsPerPage = 12

  useEffect(() => {
    if (slug) {
      loadCategoryData()
    }
  }, [slug, currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategoryData = async () => {
    if (!slug) return
    
    try {
      setIsLoading(true)
      const [postsResponse, categoriesResponse] = await Promise.all([
        postsAPI.getByCategory(slug, { 
          page: currentPage, 
          limit: postsPerPage 
        }),
        categoriesAPI.getAll()
      ])
      
      setPosts(postsResponse.posts || [])
      setTotalPages(postsResponse.pagination?.totalPages || 1)
      
      // Find category by slug
      const foundCategory = categoriesResponse.categories.find(cat => cat.slug === slug)
      setCategory(foundCategory || null)
    } catch (error) {
      console.error('Failed to load category data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo(0, 0)
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading">Đang tải...</div>
      </div>
    )
  }

  return (
    <div className="category-page">
      <div className="container">
        {/* Category Header */}
        <div className="category-header">
          <h1 className="category-title">
            {category?.name || 'Danh mục'}
          </h1>
          {category?.description && (
            <p className="category-description">{category.description}</p>
          )}
        </div>

        {/* Posts Grid */}
        {posts.length > 0 ? (
          <div className="posts-section">
            <div className="posts-grid">
              {posts.map(post => (
                <article key={post.id} className="post-article">
                  {post.featured_image && (
                    <div className="article-image">
                      <Link to={`/post/${post.slug}`}>
                        <img src={post.featured_image} alt={post.title} />
                      </Link>
                      {post.media_type && post.media_type !== 'text' && (
                        <div className="media-badge">
                          {post.media_type === 'video' ? '📹' : '🎧'}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="article-content">
                    <div className="article-meta">
                      <span className="category">{post.category_name}</span>
                      <span className="date">{formatDate(post.publish_date)}</span>
                    </div>
                    <h3 className="article-title">
                      <Link to={`/post/${post.slug}`}>{post.title}</Link>
                    </h3>
                    {post.excerpt && (
                      <p className="article-excerpt">{post.excerpt}</p>
                    )}
                    <div className="article-footer">
                      {post.author_name && (
                        <span className="author">Tác giả: {post.author_name}</span>
                      )}
                      <span className="views">{post.view_count || 0} lượt xem</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Trang trước
                </button>
                
                <div className="pagination-info">
                  <span>Trang {currentPage} / {totalPages}</span>
                </div>
                
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Trang sau →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-posts">
            <h3>Không có bài viết nào trong danh mục này</h3>
            <p>Vui lòng thử lại sau hoặc xem các danh mục khác.</p>
            <Link to="/" className="back-home">← Quay về trang chủ</Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default CategoryPage